import React, { forwardRef, useEffect, useState } from 'react';
import Select, { FormatOptionLabelMeta, InputActionMeta, PropsValue, SingleValue, StylesConfig } from 'react-select';

import DropdownIndicator from './components/DropdownIndicator';
import './dropdown.scss';

export interface IDropdownProps<OptionType> {
  optionsData: OptionType[];
  id?: string;
  placeholder?: string | React.ReactNode;
  getOptionLabel?: (option: OptionType) => string;
  getFormatOptionLabel?: (option: OptionType, optionLabelMeta: FormatOptionLabelMeta<OptionType>) => React.ReactNode;
  getOptionValue: (option: OptionType) => string;
  handleChange?: (newValue: SingleValue<OptionType>) => void;
  handleInputChange?: (newValue: string) => void;
  isLoading?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  customStyles?: StylesConfig<OptionType>;
  selectedOption?: SingleValue<OptionType>;
  menuPortalTarget?: HTMLElement | null;
  disabled?: boolean;
  defaultValue?: PropsValue<OptionType>;
  name?: string;
  needToShowInputTextOnBlur?: boolean;
  ref?: React.Ref<any>;
}

function DropdownComponent<OptionType>(
  props: IDropdownProps<OptionType>,
  ref?: React.Ref<any>
) {
  const {
    id,
    placeholder,
    getOptionLabel,
    getFormatOptionLabel,
    getOptionValue,
    handleChange,
    handleInputChange,
    optionsData,
    isLoading,
    isSearchable = false,
    customStyles,
    selectedOption,
    menuPortalTarget,
    isClearable = false,
    disabled,
    defaultValue,
    name,
    needToShowInputTextOnBlur = true,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const [cachedOptions, setCachedOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    setCachedOptions(optionsData);
  }, [optionsData]);


  const onInputChange = (newValue: string, action: InputActionMeta) => {

    if (action.action !== 'input-blur' && action.action !== 'menu-close') {
      setInputValue(newValue);
    }

    if (typeof handleInputChange === 'function' && newValue.length > 0) {
      handleInputChange(newValue);
    } else if (!needToShowInputTextOnBlur) {
      setCachedOptions([]);
    }
  };

  return (
    <Select
      ref={ref}
      options={cachedOptions}
      className="ag-dropdown"
      classNamePrefix="ag-dropdown"
      inputId={id}
      inputValue={needToShowInputTextOnBlur ? inputValue : undefined}
      components={{ DropdownIndicator }}
      isLoading={isLoading}
      onInputChange={onInputChange}
      isClearable={isClearable}
      // TODO: TS fix. This component will be removed at all so I left is as is
      onChange={(newValue) => handleChange?.(Array.isArray(newValue) ? newValue[0] : newValue)}
      placeholder={placeholder}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={getFormatOptionLabel}
      getOptionValue={getOptionValue}
      isSearchable={isSearchable}
      styles={customStyles}
      isMulti={false}
      value={selectedOption}
      menuPortalTarget={menuPortalTarget}
      isDisabled={disabled}
      defaultValue={defaultValue}
      name={name}
    />
  );
}

// eslint-disable-next-line max-len
const Dropdown = forwardRef(DropdownComponent) as <OptionType>(props: IDropdownProps<OptionType>) => JSX.Element;

export default Dropdown;

export { default as Dropdown } from './DropdownRedesign/Dropdown';
